import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'oidc-react';
import {
  DataGridPro,
  GridColDef,
  GridPaginationModel,
  GridFooterContainer,
  GridPagination,
} from '@mui/x-data-grid-pro';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EditIcon from '@mui/icons-material/Edit';
import {
  Chip,
  Paper,
  Box,
  Switch,
  Button,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ReactComponent as TrashIcon } from '../../../img/TrashIcon.svg';
import { Report } from '../../../models/report';
import { formatDate } from '../../../helpers/dateUtils';
import { UserContext } from '../../../context/UserContext';
import { downloadGeneratedReport } from '../../../api/generatedReports';
import ConfirmDeleteDialog from '../../../components/ConfirmDeleteDialog';
import { ReactComponent as FileDownload } from '../../../img/FileDownload.svg';
import { ReactComponent as FileDownloadDarkMode } from '../../../img/FileDownloadDarkMode.svg';
import EmptyResult from '../../../components/EmptyResult';

type ModeType = 'manual' | 'scheduled' | 'scheduleManager';

const CustomFooter = () => (
  <GridFooterContainer>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        px: 1,
      }}
    >
      <Typography variant="caption" color="text.secondary" marginLeft={1}>
        {useTranslation().t('screens.dataDownloader.generatedReportsTable.footer', { count: 50 })}
      </Typography>
      <GridPagination />
    </Box>
  </GridFooterContainer>
);

type props = {
  reports: Report[];
  handleDeleteReport: (reportId: string | undefined) => void;
  handleRefresh: () => void;
  handleChangeSearch: (value: string) => void;
  handleToggle: (scheduleId: string, enabled: boolean) => void;
  onEdit: (row: Report) => void;
  searchValue: string;
};

const GeneratedReportsTable = ({
  reports,
  handleDeleteReport,
  handleRefresh,
  handleChangeSearch,
  handleToggle,
  onEdit,
  searchValue,
}: props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useContext(UserContext);
  const auth = useAuth();
  const maybeAccessToken = auth?.userData?.access_token;
  const enableScheduledFeature = process.env.REACT_APP_ENABLE_DOWNLOADER_SCHEDULED_FEATURE === 'true';

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [mode, setMode] = useState<ModeType>('manual');

  const [filteredReports, setFilteredReports] = useState<Report[]>(reports);

  const [confirmDeleteObject, setConfirmDeleteObject] = useState<Report | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleModeChange = useCallback(
    (newMode: ModeType) => {
      if (!enableScheduledFeature && (newMode === 'scheduled' || newMode === 'scheduleManager')) {
        return;
      }
      setMode(newMode);
    },
    [setMode, enableScheduledFeature]
  );

  const handleDownload = useCallback((reportId: string) => {
    downloadGeneratedReport(reportId, maybeAccessToken);
  }, [maybeAccessToken]);

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPage(model.page);
    setRowsPerPage(model.pageSize);
  }, []);

  const statusData: {
    [key: string]: { label: string; icon: React.ReactElement; bgColor: string; textColor: string };
  } = useMemo(
    () => ({
      succeeded: {
        label: t('screens.dataDownloader.generatedReportsTable.statusSucceeded'),
        icon: <CheckCircleOutlineIcon sx={{ mr: 0.5 }} />,
        bgColor: theme.palette.success['190p'] || '',
        textColor: theme.palette.success['160p'] || '',
      },
      in_progress: {
        label: t('screens.dataDownloader.generatedReportsTable.statusInProgress'),
        icon: <WarningAmberIcon sx={{ mr: 0.5 }} />,
        bgColor: theme.palette.warning['190p'] || '',
        textColor: theme.palette.warning['160p'] || '',
      },
      created: {
        label: t('screens.dataDownloader.generatedReportsTable.statusCreated'),
        icon: <InfoOutlinedIcon sx={{ mr: 0.5 }} />,
        bgColor: theme.palette.info['190p'] || '',
        textColor: theme.palette.info['160p'] || '',
      },
      failed: {
        label: t('screens.dataDownloader.generatedReportsTable.statusFailed'),
        icon: <ErrorOutlineIcon sx={{ mr: 0.5 }} />,
        bgColor: theme.palette.error['190p'] || '',
        textColor: theme.palette.error['160p'] || '',
      },
    }),
    [theme, t]
  );

  const reportColumns: GridColDef<Report>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('screens.dataDownloader.generatedReportsTable.name'),
        headerClassName: 'name-column-header',
        flex: 1,
        renderCell: ({ row }) => (
          <Box sx={{ marginLeft: '16px', display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography fontWeight={600}>{row.name}</Typography>
          </Box>
        ),
      },
      {
        field: 'generationDate',
        headerName: t('screens.dataDownloader.generatedReportsTable.generatedOn'),
        flex: 1,
        valueGetter: (value, row: Report) =>
          row.generationDate ?
            formatDate(row.generationDate, locale, {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }) : '',
      },
      {
        field: 'requestDate',
        headerName: t('screens.dataDownloader.generatedReportsTable.createdOn'),
        flex: 1,
        valueGetter: (value, row: Report) =>
          formatDate(row.requestDate, locale, {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          }),
      },
      {
        field: 'status',
        headerName: t('screens.dataDownloader.generatedReportsTable.status'),
        headerClassName: 'status-column-header',
        headerAlign: 'center',
        flex: 1,
        align: 'center',
        renderCell: ({ row }) => {
          const config = statusData[row.status];
          return (
            <Chip
              size="small"
              icon={config.icon}
              label={config.label}
              sx={{
                backgroundColor: config.bgColor,
                color: config.textColor,
                '& .MuiChip-icon': {
                  color: config.textColor,
                },
              }}
            />
          );
        },
      },
      {
        field: 'recurrence',
        headerName: t('screens.dataDownloader.generatedReportsTable.recurrence'),
        flex: 0.75,
        renderCell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography>
              {t(`screens.dataDownloader.generatedReportsTable.recurrenceOptions.${row.recurrence}`)}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'filters',
        headerName: t('screens.dataDownloader.generatedReportsTable.filters'),
        flex: 1,
        renderCell: ({ row }) => {
          const { locations, devices, staff } = row.filters;
          const value = [
            locations.map((l) => l.name).join(', '),
            devices.map((d) => d.name).join(', '),
            staff.map((s) => s.name).join(', '),
          ]
            .filter((part) => part.length > 0)
            .join(', ');
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2">{value}</Typography>
            </Box>
          );
        },
      },
      {
        field: 'enabled',
        headerName: t('screens.dataDownloader.generatedReportsTable.enabled'),
        renderCell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Switch
              checked={row.isEnabled}
              size="medium"
              onChange={() => handleToggle(row.id, !row.isEnabled)}
              role='switch'
              data-testid={`enabled-${row.name}`}
            />
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: t('screens.dataDownloader.generatedReportsTable.actions'),
        renderCell: ({ row }) => {
          const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            setConfirmDeleteObject(row);
            setConfirmDeleteOpen(true);
          };

          return (
            <Box sx={{ display: 'grid', gridTemplateAreas: '"action delete"', gridTemplateColumns: '1fr 1fr', columnGap: 1, placeItems: 'center', width: '100%' }}>
              {mode !== 'scheduleManager' && row.status === 'succeeded' && (
                <IconButton
                  data-testid={row.status === 'succeeded' ? 'download' : undefined}
                  onClick={row.status === 'succeeded' ? () => handleDownload(row.id) : undefined}
                  disabled={row.status !== 'succeeded'}
                  size="small"
                  sx={{
                    gridArea: 'action',
                    '& path': {
                      fill: row.status === 'succeeded' ? undefined : theme.palette.grey['30p'],
                    },
                  }}
                >
                  {theme.palette.mode === 'dark'
                    ? <FileDownloadDarkMode />
                    : <FileDownload />}
                </IconButton>
              )}

              {mode === 'scheduleManager' && (
                <IconButton
                  data-testid="edit"
                  onClick={() => onEdit(row)}
                  size="small"
                  sx={{ gridArea: 'action' }}
                >
                  <EditIcon />
                </IconButton>
              )}

              <Tooltip
                title={t('actions.delete')}
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: theme.palette.error['160p'],
                      color: '#fff',
                    },
                  },
                  arrow: {
                    sx: {
                      color: theme.palette.error['160p'],
                    },
                  },
                }}
              >
                <IconButton
                  onClick={handleOpen}
                  size="small"
                  sx={{
                    gridArea: 'delete',
                    '&:hover': {
                      backgroundColor: theme.palette.error['190p'],
                      '& .MuiSvgIcon-root': {
                        color: theme.palette.error['160p'],
                      },
                    },
                  }}
                  role="button"
                  aria-label={`delete-${row.name}`}
                  data-testid='delete'
                >
                  <DeleteOutlineIcon color="error" cursor="pointer" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [t, locale, statusData, handleDownload, theme, mode, handleToggle, onEdit]
  );

  const visibleColumns = useMemo(() => {
    const layouts = {
      manual: ['name', 'generationDate', 'status', 'actions'],
      scheduled: ['name', 'generationDate', 'status', 'recurrence', 'actions'],
      scheduleManager: ['name', 'requestDate', 'recurrence', 'filters', 'enabled', 'actions'],
    }

    const layout = layouts[mode] || [];
    const filteredColumns = reportColumns.filter((c) => layout.includes(c.field));

    return filteredColumns;
  }, [mode, reportColumns]);

  useEffect(() => {
    if (!enableScheduledFeature && (mode === 'scheduled' || mode === 'scheduleManager')) {
      setMode('manual');
      return;
    }

    const filtered = mode === 'manual' ? reports?.filter((report) => !report.isAutomated) : reports?.filter((report) => report.isAutomated);
    setFilteredReports(filtered);
  }, [mode, reports, searchValue, enableScheduledFeature]);

  return (
    <Box
      sx={{
        flex: 1,
        width: '100%',
        height: '100%',
      }}
    >
      <Paper
        sx={{
          height: '70vh',
          width: '100%',
          display: 'flex',
          flexFlow: 'column',
          borderRadius: 3,
          overflow: 'hidden',
        }}
        variant="outlined"
      >

        <Box sx={{ display: 'flex', alignItems: 'center', height: 64 }}>
          {enableScheduledFeature && (
            <>
              <Button
                variant="text"
                onClick={() => handleModeChange('manual')}
                sx={{
                  height: '100%',
                  borderBottom: `3px solid transparent`,
                  borderColor: mode === 'manual' ? `${theme.palette.primary.main}` : 'transparent',
                  color:
                    mode === 'manual'
                      ? `${theme.palette.primary.main}`
                      : `${theme.palette.text.secondary}`,
                  borderRadius: 0,
                  fontWeight: 600,
                }}
                role="tab"
                name="manual"
              >
                {t('screens.dataDownloader.generatedReportsTable.manual')}
              </Button>
              <Button
                variant="text"
                onClick={() => handleModeChange('scheduled')}
                sx={{
                  height: '100%',
                  borderBottom: `3px solid transparent`,
                  borderColor: mode === 'scheduled' ? `${theme.palette.primary.main}` : 'transparent',
                  color:
                    mode === 'scheduled'
                      ? `${theme.palette.primary.main}`
                      : `${theme.palette.text.secondary}`,
                  borderRadius: 0,
                  fontWeight: 600,
                }}
                role="tab"
                name="scheduled"
              >
                {t('screens.dataDownloader.generatedReportsTable.scheduled')}
              </Button>
              <Button
                variant="text"
                onClick={() => handleModeChange('scheduleManager')}
                sx={{
                  height: '100%',
                  borderBottom: `3px solid transparent`,
                  borderColor:
                    mode === 'scheduleManager' ? `${theme.palette.primary.main}` : 'transparent',
                  color:
                    mode === 'scheduleManager'
                      ? `${theme.palette.primary.main}`
                      : `${theme.palette.text.secondary}`,
                  borderRadius: 0,
                  fontWeight: 600,
                }}
                role="tab"
                name="scheduleManager"
              >
                <ScheduleIcon sx={{ mr: 1 }} />
                {t('screens.dataDownloader.generatedReportsTable.scheduleManager')}
              </Button>
            </>
          )}

          <Box
            sx={{
              display: 'inline-block',
              marginLeft: 'auto',
              marginRight: '12px',
            }}
          >
            <TextField
              placeholder={t('screens.dataDownloader.generatedReportsTable.searchBox')}
              size="small"
              sx={{ marginRight: 'auto' }}
              id="search-input"
              InputProps={{
                inputProps: { 'data-qa-id': 'tableSearchInput', 'data-testid': 'tableSearchInput' },
                endAdornment:
                  searchValue === '' ? (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ) : (
                    <InputAdornment position="end">
                      <IconButton
                        title={t('actions.delete')}
                        onClick={() => {
                          handleChangeSearch('');
                        }}
                        sx={{ padding: 0 }}
                        data-qa-id="tableSearchInputClearButton"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
              }}
              value={searchValue}
              onChange={(e) => handleChangeSearch(e.target.value)}
              fullWidth={false}
            />
          </Box>
          <Button
            variant="text"
            onClick={() => handleRefresh()}
            startIcon={<RefreshIcon />}
            sx={{
              height: '100%',
              color: theme.palette.text.secondary,
              borderRadius: 0,
              fontWeight: 600,
            }}
            role="button"
            name="refresh"
          >
            {t('screens.dataDownloader.generatedReportsTable.refresh')}
          </Button>
        </Box>

        {(!filteredReports || filteredReports?.length === 0) && (
          <EmptyResult
            title={t('components.iconMessage.noReports.title')}
            subtitle={t('components.iconMessage.noReports.subtitle')}
            sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
            button={{
              text: t('components.iconMessage.noReports.button'),
              to: '/reports-overview',
            }}
          />
        )}
        {filteredReports?.length && (
          <DataGridPro
            disableVirtualization
            rows={filteredReports ?? []}
            columns={visibleColumns}
            getRowId={(row) => row.id}
            pagination
            getRowHeight={() => 'auto'}
            pageSizeOptions={[10, 25, 50, 100]}
            onPaginationModelChange={handlePaginationModelChange}
            initialState={{
              pagination: {
                paginationModel: {
                  page,
                  pageSize: rowsPerPage,
                },
              },
            }}
            localeText={{
              MuiTablePagination: {
                labelRowsPerPage: t('components.filters.rowsPerPage'),
              },
              footerRowSelected: () => '',
            }}
            disableColumnMenu
            disableMultipleRowSelection
            slots={{
              footer: CustomFooter,
            }}
            sx={{
              flex: 1,
              width: '100%',
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              '& .MuiDataGrid-row:hover': {
                backgroundColor: theme.palette.secondary.main,
                transition: 'background-color 0.2s ease',
              },
              '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within, .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
                outline: 'none',
                backgroundColor: theme?.palette?.grey?.['4p'],
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                fontSize: 16,
              },
              '& .name-column-header .MuiDataGrid-columnHeaderTitle': {
                paddingLeft: '16px',
              },
              '& .MuiDataGrid-columnHeader, .MuiDataGrid-scrollbarFiller, .MuiDataGrid-filler': {
                backgroundColor: 'background.paper',
              },
              '& ::-webkit-scrollbar': {
                width: '6px',
                height: '6px',
              },
              '& ::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: theme?.palette?.grey?.['4p'],
              },
              '& .MuiDataGrid-cell': {
                minHeight: '52px',
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-cell:focus': {
                minHeight: '52px',
                backgroundColor: theme?.palette?.grey?.['4p'],
                display: 'flex',
                alignItems: 'center',
              },
              '& .status-column-header': {
                '&:hover .MuiDataGrid-columnHeaderTitle, &.MuiDataGrid-columnHeader--sorted .MuiDataGrid-columnHeaderTitle': {
                  paddingLeft: '28px',
                },
                '.MuiDataGrid-columnHeaderTitle': {
                  paddingLeft: 0,
                },
              },
            }}
          />
        )}
      </Paper>

      <ConfirmDeleteDialog
        open={Boolean(confirmDeleteObject?.id) && confirmDeleteOpen}
        dialog={{
          title: t('components.confirmDeleteDialog.title', { name: confirmDeleteObject?.name }),
          content: t('components.confirmDeleteDialog.content', { item: 'report' }),
        }}
        actions={{
          close: () => setConfirmDeleteOpen(false),
          delete: () => {
            handleDeleteReport(confirmDeleteObject?.id);
            setConfirmDeleteOpen(false);
          },
        }}
        icon={
          <TrashIcon
            style={{ fontSize: 56, color: theme.palette.error['160p'], padding: '8px' }}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            width="1em"
            height="1em"
          />
        }
      />
    </Box>
  );
};

export default GeneratedReportsTable;
